import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, ExternalLink, Sparkles, Copy, Check, Pencil, X, Save, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getToolUrl } from '@/lib/toolUrls';
import { CarryForwardPanel } from './CarryForwardPanel';
import type { CarryForwardSource } from '@/lib/carryForward';
import type { PromptDraftPayload } from '@/lib/workflowStreamProtocol';

interface PromptArtifactCardProps {
  draft: PromptDraftPayload;
  /** Only the latest version of a step's prompt is editable. */
  editable?: boolean;
  /** Notify parent when this card enters/exits edit mode (so the parent can hide other UI). */
  onEditingChange?: (editing: boolean) => void;
  /** Commit an edited prompt body. The parent bumps the version and re-renders. */
  onSaveEdit?: (newBody: string) => void;
  /** Submit a "remix" — additional context for Cosmo to fold into a new draft. */
  onRemix?: (additionalContext: string) => void;
  /** True while a remix request is in flight. */
  remixing?: boolean;
  /** Prior step outputs the user can append/download into this prompt. */
  carryForwardSources?: CarryForwardSource[];
  workflowName?: string | null;
  /** Append an inline carry-forward block to the prompt body (bumps version). */
  onInlineCarryForward?: (newBody: string, source: CarryForwardSource) => void;
  /** When true (older versions in the stream), start collapsed showing only the header. */
  collapsedByDefault?: boolean;
}

export function PromptArtifactCard({
  draft,
  editable,
  onEditingChange,
  onSaveEdit,
  onRemix,
  remixing,
  carryForwardSources,
  workflowName,
  onInlineCarryForward,
  collapsedByDefault,
}: PromptArtifactCardProps) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(draft.body);
  const [remixOpen, setRemixOpen] = useState(false);
  const [remixText, setRemixText] = useState('');
  // Older versions in the stream start collapsed; the user can expand to peek.
  const [collapsed, setCollapsed] = useState(!!collapsedByDefault);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const remixRef = useRef<HTMLTextAreaElement>(null);
  const url = getToolUrl(draft.tool);

  // Focus the remix textarea when it opens; close on remix success
  // (parent bumps the draft prop → effect runs).
  useEffect(() => {
    if (remixOpen) {
      requestAnimationFrame(() => {
        if (remixRef.current) {
          const ta = remixRef.current;
          ta.focus();
          ta.style.height = 'auto';
          ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
        }
      });
    }
  }, [remixOpen]);
  useEffect(() => {
    // Reset remix UI when a new draft version arrives.
    if (!remixing) {
      setRemixOpen(false);
      setRemixText('');
    }
  }, [draft.version, remixing]);

  // When the draft prop changes (new version arrived), refresh local state
  // if we're not actively editing — otherwise we'd lose in-progress edits.
  useEffect(() => {
    if (!editing) setEditBody(draft.body);
  }, [draft.body, editing]);

  // Auto-resize textarea while editing.
  useEffect(() => {
    if (!editing || !textareaRef.current) return;
    const ta = textareaRef.current;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 480)}px`;
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
  }, [editing]);

  const startEdit = () => {
    if (!editable) return;
    setEditBody(draft.body);
    setEditing(true);
    onEditingChange?.(true);
  };

  const cancelEdit = () => {
    setEditBody(draft.body);
    setEditing(false);
    onEditingChange?.(false);
  };

  const saveEdit = () => {
    const trimmed = editBody.trim();
    if (!trimmed || trimmed === draft.body.trim()) {
      cancelEdit();
      return;
    }
    onSaveEdit?.(trimmed);
    setEditing(false);
    onEditingChange?.(false);
  };

  const handleRun = async () => {
    try {
      await navigator.clipboard.writeText(draft.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.success(`Prompt copied — opening ${draft.tool}`);
      } else {
        toast.success('Prompt copied to clipboard');
      }
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleCopyOnly = async () => {
    try {
      await navigator.clipboard.writeText(draft.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success('Prompt copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const submitRemix = () => {
    const text = remixText.trim();
    if (!text || remixing || !onRemix) return;
    onRemix(text);
  };

  const handleRemixKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submitRemix();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setRemixOpen(false);
      setRemixText('');
    }
  };

  // Collapsed mode: show only the header bar with click-to-expand affordance.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        className="w-full rounded-lg border border-border/60 bg-card/40 hover:bg-card/80 hover:border-primary/20 transition-colors flex items-center justify-between gap-2 px-3 py-1.5 text-xs text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="truncate text-muted-foreground">
            <span className="font-medium text-foreground">v{draft.version}</span> · {draft.title}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0 text-[11px] text-muted-foreground">
          <span className="hidden sm:inline">{draft.tool}</span>
          <ChevronDown className="h-3 w-3" />
        </div>
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-primary/30 bg-gradient-to-b from-primary/5 to-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-primary/20 bg-primary/8">
        <div className="flex items-center gap-2 text-xs min-w-0">
          <Badge variant="outline" className="bg-primary/15 text-primary border-primary/30 h-5 px-1.5 text-[10px]">
            <FileText className="h-2.5 w-2.5 mr-0.5" />
            prompt
          </Badge>
          <span className="font-medium truncate">{draft.title}</span>
          <span className="text-muted-foreground text-[11px] shrink-0">
            v{draft.version}{editing ? ' · editing' : ''}
          </span>
          {collapsedByDefault && (
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              className="text-[10px] text-muted-foreground hover:text-foreground ml-1"
              title="Collapse"
            >
              <ChevronDown className={cn('h-3 w-3 transition-transform', 'rotate-180')} />
            </button>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0">{draft.tool}</span>
      </div>

      {editing ? (
        <textarea
          ref={textareaRef}
          value={editBody}
          onChange={(e) => setEditBody(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full px-3 py-3 text-[12px] font-mono leading-relaxed bg-card border-0 border-y border-primary/20 focus:outline-none focus:ring-0 resize-none"
          style={{ minHeight: '160px' }}
        />
      ) : (
        <pre className="px-3 py-3 text-[12px] font-mono whitespace-pre-wrap max-h-72 overflow-auto leading-relaxed">{draft.body}</pre>
      )}

      {draft.toolNotes && !editing && !remixOpen && (
        <div className="px-3 pb-2 text-[11px] text-muted-foreground italic border-b border-border/30">
          {draft.toolNotes}
        </div>
      )}

      {editable && !editing && !remixOpen && carryForwardSources && carryForwardSources.length > 0 && onInlineCarryForward && (
        <CarryForwardPanel
          sources={carryForwardSources}
          workflowName={workflowName ?? null}
          onInline={(block, src) => onInlineCarryForward(draft.body + block, src)}
        />
      )}

      {/* Remix context input — appears when user clicks Remix */}
      {remixOpen && !editing && (
        <div className="px-3 py-3 border-t border-primary/20 bg-card space-y-2">
          <div className="flex items-center gap-1.5 text-xs">
            <Sparkles className="h-3 w-3 text-primary" />
            <span className="font-medium">What additional context should I add?</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Tell Cosmo what to refine — specific filters, missing constraints, a different angle. Cosmo will either ask one follow-up or produce a new version.
          </p>
          <textarea
            ref={remixRef}
            value={remixText}
            onChange={(e) => {
              setRemixText(e.target.value);
              const ta = e.target;
              ta.style.height = 'auto';
              ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
            }}
            onKeyDown={handleRemixKeyDown}
            placeholder="e.g. focus on enterprise SaaS in EMEA, include verified emails only…"
            disabled={remixing}
            rows={2}
            className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50 resize-none"
            style={{ minHeight: '52px', maxHeight: '200px' }}
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              <kbd className="px-1 py-0.5 border border-border rounded font-mono">esc</kbd> cancel ·{' '}
              <kbd className="px-1 py-0.5 border border-border rounded font-mono">⌘↵</kbd> send
            </span>
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => { setRemixOpen(false); setRemixText(''); }} disabled={remixing} className="h-7 text-xs">
                Cancel
              </Button>
              <Button size="sm" onClick={submitRemix} disabled={!remixText.trim() || remixing} className="h-7 text-xs gap-1">
                {remixing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                {remixing ? 'Remixing…' : 'Remix'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-card">
        {editing ? (
          <>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <kbd className="px-1.5 py-0.5 border border-border rounded font-mono">esc</kbd>
              <span>cancel</span>
              <span className="mx-1">·</span>
              <kbd className="px-1.5 py-0.5 border border-border rounded font-mono">⌘↵</kbd>
              <span>save</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={cancelEdit} className="h-7 text-xs gap-1">
                <X className="h-3 w-3" />
                Cancel
              </Button>
              <Button size="sm" onClick={saveEdit} disabled={!editBody.trim() || editBody.trim() === draft.body.trim()} className="h-7 text-xs gap-1">
                <Save className="h-3 w-3" />
                Save changes
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <kbd className="px-1.5 py-0.5 border border-border rounded font-mono">C</kbd>
              <span>copy</span>
              <span className="mx-1">·</span>
              <kbd className="px-1.5 py-0.5 border border-border rounded font-mono">⌘↵</kbd>
              <span>run</span>
            </div>
            <div className="flex items-center gap-1.5">
              {editable && onSaveEdit && (
                <Button variant="outline" size="sm" onClick={startEdit} className="h-7 text-xs gap-1">
                  <Pencil className="h-3 w-3" />
                  Edit
                </Button>
              )}
              {editable && onRemix && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRemixOpen(true)}
                  disabled={remixing}
                  className="h-7 text-xs gap-1"
                >
                  <Sparkles className="h-3 w-3" />
                  Remix
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleCopyOnly} className="h-7 text-xs gap-1">
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                Copy
              </Button>
              <Button size="sm" onClick={handleRun} className="h-7 text-xs gap-1">
                Run in {draft.tool}
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
